import React from 'react';
import { } from 'antd';
import VideoQuizSection from './VideoQuizSection';
import FlashCardSection from './FlashCardSection';
import WordChoicePartWriting from './WordChoicePartWriting';
import DiagramSection from './DiagramSection';
import FillBlankWritingSection from './FillBlankWritingSection';
import FreeWritingSection from './FreeWritingSection';
import StoryDialogSection from './StoryDialogSection';
import StoryDialogQuestionSection from './StoryDialogQuestionSection';

// const { Option } = Select; // 미사용

interface VideoQuiz {
  id: string;
  question: string;
  choices: string[];
  answer: number | number[];
  reason: string;
  allowMultiple?: boolean;
}

interface UnitTypeFieldsProps {
  selectedType: string;
  videoQuizzes: VideoQuiz[];
  videoQuizEnabled: boolean;
  onVideoQuizEnabledChange: (enabled: boolean) => void;
  onAddVideoQuiz: () => void;
  onRemoveVideoQuiz: (quizId: string) => void;
  onUpdateQuizQuestion: (quizId: string, question: string) => void;
  onUpdateQuizReason: (quizId: string, reason: string) => void;
  onAddQuizChoice: (quizId: string) => void;
  onRemoveQuizChoice: (quizId: string, choiceIndex: number) => void;
  onUpdateQuizChoice: (quizId: string, choiceIndex: number, value: string) => void;
  onSetQuizAnswer: (quizId: string, answerIndex: number) => void;
  onToggleQuizMultiple: (quizId: string, allowMultiple: boolean) => void;
  // 플래시 카드 관련 props
  recordingEnabled: boolean; // 기존 공통(하위 호환)
  onRecordingEnabledChange: (enabled: boolean) => void; // 기존 공통(하위 호환)
  // 섹션별 녹음 여부(충돌 방지용)
  flashRecordingEnabled?: boolean;
  onFlashRecordingEnabledChange?: (enabled: boolean) => void;
  wordChoiceRecordingEnabled?: boolean;
  onWordChoiceRecordingEnabledChange?: (enabled: boolean) => void;
  onWordImageUpload?: (id: string, url: string) => void; // 미사용 가능
  onMeaningImageUpload?: (id: string, url: string) => void; // 미사용 가능
  onWordImageFileChange?: (id: string, file: File) => void;
  onMeaningImageFileChange?: (id: string, file: File) => void;
  // 단어 선택 관련 props
  onWordSelectionChange?: (selectedWords: string[]) => void;
  // 단어 선택 & 녹음 관련 props
  onWordChoiceChange?: (selectedWords: string[]) => void;
  onWordChoiceRecordingChange?: (isRecording: boolean) => void;
  // 부분 영작 관련 props
  onTitleTranslationChange?: (value: string) => void;
  onCorrectSentenceChange?: (value: string) => void;
  // 다이어그램 관련 props
  onDiagramCategoryChange?: (sectionIndex: number, category: string) => void;
  onDiagramWordChange?: (sectionIndex: number, words: string[]) => void;
  // 다이어그램 초기 데이터
  diagramCategories?: string[];
  diagramWords?: string[][];
  // 빈칸 채우기 관련 props
  onFullTextChange?: (value: string) => void;
  onTranslationChange?: (value: string) => void;
  onCorrectWordChange?: (value: string) => void;
  // 프리라이팅 관련 props
  onKeywordChange?: (value: string) => void;
  onWritingCountChange?: (value: number | null) => void;
  onHintChange?: (value: string) => void;
  // 플래시카드 초기 로드 데이터
  flashCardInitial?: Array<{ word: string; meaning: string; wordImageUrl?: string }>;
  // 단어고르기 초기 로드 데이터
  wordChoiceInitial?: Array<{ word: string; wordImageUrl?: string; titleTranslation?: string }>;
}

const UnitTypeFields: React.FC<UnitTypeFieldsProps> = ({
  selectedType,
  videoQuizzes,
  videoQuizEnabled,
  onVideoQuizEnabledChange,
  onAddVideoQuiz,
  onRemoveVideoQuiz,
  onUpdateQuizQuestion,
  onUpdateQuizReason,
  onAddQuizChoice,
  onRemoveQuizChoice,
  onUpdateQuizChoice,
  onSetQuizAnswer,
  onToggleQuizMultiple,
  recordingEnabled,
  onRecordingEnabledChange,
  flashRecordingEnabled,
  onFlashRecordingEnabledChange,
  wordChoiceRecordingEnabled,
  onWordChoiceRecordingEnabledChange,
  onWordImageUpload,
  onMeaningImageUpload,
  onWordImageFileChange,
  onMeaningImageFileChange,
  onTitleTranslationChange,
  onCorrectSentenceChange,
  onDiagramCategoryChange,
  onDiagramWordChange,
  diagramCategories,
  diagramWords,
  onFullTextChange,
  onTranslationChange,
  onCorrectWordChange,
  onKeywordChange,
  onWritingCountChange,
  onHintChange
  ,flashCardInitial,
  wordChoiceInitial
}) => {
  // 공통 섹션 렌더링 함수들
  const renderVideoQuizFields = (title?: string) => (
    <div className="mb-8">
      <VideoQuizSection
        videoQuizzes={videoQuizzes}
        videoQuizEnabled={videoQuizEnabled}
        onVideoQuizEnabledChange={onVideoQuizEnabledChange}
        onAddVideoQuiz={onAddVideoQuiz}
        onRemoveVideoQuiz={onRemoveVideoQuiz}
        onUpdateQuizQuestion={onUpdateQuizQuestion}
        onUpdateQuizReason={onUpdateQuizReason}
        onAddQuizChoice={onAddQuizChoice}
        onRemoveQuizChoice={onRemoveQuizChoice}
        onUpdateQuizChoice={onUpdateQuizChoice}
        onSetQuizAnswer={onSetQuizAnswer}
        onToggleAllowMultiple={onToggleQuizMultiple}
        title={title}
        // videoUrl은 등록 화면의 상위 폼에서 관리하므로 VideoQuizSection 내부 input에서 Form.Item name="videoUrl" 사용
      />
    </div>
  );

  const renderFlashCardFields = (title?: string) => (
    <div className="mb-8">
      <FlashCardSection
        recordingEnabled={flashRecordingEnabled ?? recordingEnabled}
        onRecordingEnabledChange={onFlashRecordingEnabledChange ?? onRecordingEnabledChange}
        initialWords={flashCardInitial}
        onWordImageUpload={onWordImageUpload}
        onMeaningImageUpload={onMeaningImageUpload}
        onWordImageFileChange={onWordImageFileChange}
        onMeaningImageFileChange={onMeaningImageFileChange}
        title={title}
      />
    </div>
  );

  const renderFlashCardFieldsEssay = (title?: string) => (
    <div className="mb-8">
      <FlashCardSection
        recordingEnabled={flashRecordingEnabled ?? recordingEnabled}
        onRecordingEnabledChange={onFlashRecordingEnabledChange ?? onRecordingEnabledChange}
        initialWords={flashCardInitial}
        enableImageUpload={false}
        title={title}
      />
    </div>
  );

  const renderWordChoicePartWriting = (title?: string) => (
    <div className="mb-8">
      <WordChoicePartWriting
        onTitleTranslationChange={onTitleTranslationChange}
        onCorrectSentenceChange={onCorrectSentenceChange}
        recordingEnabled={wordChoiceRecordingEnabled ?? recordingEnabled}
        onRecordingEnabledChange={onWordChoiceRecordingEnabledChange ?? onRecordingEnabledChange}
        initialWords={wordChoiceInitial}
        selectionType={'sentence'}
        title={title}
      />
    </div>
  );

  const renderDiagramSection = () => (
    <div className="mb-8">
      <DiagramSection
        onCategoryChange={onDiagramCategoryChange}
        onWordChange={onDiagramWordChange}
        initialCategories={diagramCategories}
        initialWords={diagramWords}
      />
    </div>
  );

  const renderFillBlankWritingSection = (title?: string) => (
    <div className="mb-8">
      <FillBlankWritingSection
        onFullTextChange={onFullTextChange}
        onTranslationChange={onTranslationChange}
        onCorrectWordChange={onCorrectWordChange}
        recordingEnabled={recordingEnabled}
        onRecordingEnabledChange={onRecordingEnabledChange}
        title={title}
      />
    </div>
  );

  const renderFreeWritingSection = (title?: string) => (
    <div className="mb-8">
      <FreeWritingSection
        onKeywordChange={onKeywordChange}
        onWritingCountChange={onWritingCountChange}
        onHintChange={onHintChange}
        title={title}
      />
    </div>
  );

  const renderStoryDialogSection = () => (
    <div className="mb-8">
      <StoryDialogSection />
    </div>
  );

  const renderStoryDialogQuestionSection = () => (
    <div className="mb-8">
      <StoryDialogQuestionSection />
    </div>
  );

  // 유형별 입력 항목 렌더링
  switch (selectedType) {
    case '파닉스데이':
      return (
        <div className="space-y-8">
          {renderVideoQuizFields()}
          {renderFlashCardFields('학습단어')}
        </div>
      );
    
    case '스타터_파닉스':
      return (
        <div className="space-y-8">
          {renderVideoQuizFields()}
          {renderFlashCardFields()}
        </div>
      );
    
    case '스타터_스트럭처':
      return (
        <div className="space-y-8">
          {renderVideoQuizFields()}
          {renderFlashCardFields('학습단어')}
          {renderDiagramSection()}
          {renderWordChoicePartWriting('부분영작 & 녹음')}
        </div>
      );
    
    case '스타터_뉘앙스':
      return (
        <div className="space-y-8">
          {renderVideoQuizFields()}
          {renderFlashCardFields('학습단어')}
          {renderWordChoicePartWriting()}
        </div>
      );
    
    case '에세이':
      return (
        <div className="space-y-8">
          {renderVideoQuizFields()}
          {renderFlashCardFieldsEssay('학습단어')}
          {renderFillBlankWritingSection()}
          {renderFreeWritingSection()}
        </div>
      );
    
    case '통번역':
      return (
        <div className="space-y-8">
          {renderFlashCardFieldsEssay()}
          {renderFillBlankWritingSection()}
          {renderFreeWritingSection()}
        </div>
      );
    
    case '리딩북':
      return (
        <div className="space-y-8">
          {renderFlashCardFields('학습단어')}
          {renderStoryDialogSection()}
          {renderStoryDialogQuestionSection()}
        </div>
      );
    
    default:
      return null;
  }
};

export default UnitTypeFields; 